// In FilterNavbarCategories.jsx
import React, { useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { scrollToShop, formatCategory } from '../../util';
import { useConfig } from '../../contexts/ConfigContext';

const FilterNavbarCategories = ({
    filters = [],
    selectedFilters = {},
    setSelectedFilters,
    menuSlug,
    specialsExist,
    menuFilter,
    handleAllClick
}) => {
    const scrollContainerRef = useRef(null);
    const navigate = useNavigate();
    const params = useParams();
    
    const { menuConfig = {} } = useConfig();
    const { specialsName } = menuConfig;
    const safeSpecialsName = specialsName || 'Deals';
    
    // Helper to check if a filter is currently selected
    const isFilterSelected = (filterName) => {
        if (!selectedFilters) return false;
        
        // For "All" button - check if we're at the default menuFilter state
        if (filterName === 'All') {
            return JSON.stringify(selectedFilters) === JSON.stringify(menuFilter);
        }
        
        // Check for specials
        if (filterName === 'specials') {
            return selectedFilters.specials === true;
        }
        
        // For category filters
        return selectedFilters.category === filterName;
    };

    // Create a synthetic event for handleChange
    const handleCategoryClick = (value) => {
        console.log('Clicking category with value:', value);
        
        // First, create the new filters state
        let newFilters = { ...selectedFilters };
        
        if (value === 'specials') {
            newFilters = {
                ...menuFilter,
                specials: true,
                category: '',
                staff_picks: false
            };
        } else {
            newFilters = {
                ...menuFilter,
                category: value,
                specials: false,
                staff_picks: false
            };
        }
        
        // Update the state FIRST
        setSelectedFilters(newFilters);
        
        // THEN navigate with setFilters: false to prevent reset
        let path = `/shop/${params.locationSlug || menuSlug}/`;
        
        if (value === 'specials') {
            path += 'specials/';
        } else if (value !== 'All' && value) {
            path += `category/${value.toLowerCase().replace(/_/g, '-')}/`;
        }
        
        // Navigate immediately (no setTimeout needed)
        navigate(path, { state: { setFilters: false } });
        scrollToShop();
    };
    
    // Handle "All" button click
    const handleAllClickLocal = () => {
        // Use the context's handleAllClick if available, otherwise fallback to local logic
        if (handleAllClick) {
            handleAllClick();
        } else {
            setSelectedFilters(menuFilter);
        }
        
        // Then navigate
        navigate(`/shop/${params.locationSlug || menuSlug}/`, { state: { setFilters: false } });
        scrollToShop();
    };

    return (
        <div className="filter-navbar-categories-container">
            <nav 
                className="filter-navbar-categories-wrapper"
                role="navigation"
                aria-label="Product category filters"
                ref={scrollContainerRef}
            >
                {/* All Button */}
                <button
                    onClick={handleAllClickLocal}
                    aria-label="Show all products"
                    aria-pressed={isFilterSelected('All')}
                    className={`filter-navbar-category-button ${isFilterSelected('All') ? 'active' : ''}`}
                >
                    All
                </button>
                
                {/* Specials Button */}
                {specialsExist && (
                    <button
                        onClick={() => handleCategoryClick('specials')}
                        className={`filter-navbar-category-button ${isFilterSelected('specials') ? 'active' : ''}`}
                        aria-label="View specials"
                        aria-pressed={isFilterSelected('specials')}
                    >
                        {safeSpecialsName}
                    </button>
                )}
                
                {/* All Categories displayed horizontally */}
                {Array.isArray(filters) && filters.map((filter) => {
                    const categoryValue = typeof filter === 'string' ? filter : filter?.toString();
                    
                    if (!categoryValue || categoryValue === 'SPECIALS' || categoryValue === 'DEALS') {
                        return null;
                    }
                    
                    return (
                        <button
                            key={categoryValue}
                            onClick={() => handleCategoryClick(categoryValue)}
                            aria-pressed={isFilterSelected(categoryValue)}
                            aria-label={`Filter by ${formatCategory(categoryValue)}`}
                            className={`filter-navbar-category-button ${isFilterSelected(categoryValue) ? 'active' : ''}`}
                        >
                            {formatCategory(categoryValue)}
                        </button>
                    );
                })}
            </nav>
        </div>
    );
};

export default FilterNavbarCategories;
